import { clientSessionViewStore } from '../../services/clientSessionViewStore.js'
import {
  ensureSessionCapability,
  SESSION_HEADER,
} from '../core/session/session-identity.js'
import type { WorkshopIntake } from '../core/workshop-pack/intake-conversation.js'
import { acceptSessionProjectionEnvelope } from './accept-session-projection-response.js'
import { logger } from './logger.js'

async function refreshProjection(sessionId: string): Promise<void> {
  await ensureSessionCapability(sessionId)
  const response = await fetch('/api/session', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { [SESSION_HEADER]: sessionId },
    cache: 'no-store',
  })
  if (!response.ok) return
  acceptSessionProjectionEnvelope(await response.json(), '/api/session:workshop-refresh')
}

// A busy or moved session is normal while other writers run (research, Live).
// Same command id on every try, so a repeat cannot store the answers twice.
export const WORKSHOP_INTAKE_ATTEMPTS = 4
const RETRY_BASE_MS = 250

/** Store consented workshop answers on the canonical session. No model call. */
export async function submitWorkshopIntake(
  sessionId: string,
  intake: WorkshopIntake
): Promise<void> {
  await ensureSessionCapability(sessionId)
  const commandId = globalThis.crypto.randomUUID()

  for (let attempt = 0; attempt < WORKSHOP_INTAKE_ATTEMPTS; attempt += 1) {
    const current = clientSessionViewStore.getServerProjectionSnapshot()
    if (!current || current.sessionId !== sessionId) await refreshProjection(sessionId)
    const envelope = clientSessionViewStore.getServerProjectionSnapshot()
    if (!envelope || envelope.sessionId !== sessionId) {
      throw new Error('Canonical session projection is unavailable')
    }

    const response = await fetch('/api/session', {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [SESSION_HEADER]: sessionId,
      },
      body: JSON.stringify({
        sessionId,
        commandId,
        expectedVersion: envelope.version,
        workshopIntake: intake,
      }),
    })

    if ((response.status === 409 || response.status === 423) && attempt < WORKSHOP_INTAKE_ATTEMPTS - 1) {
      if (attempt > 0) await new Promise(resolve => setTimeout(resolve, RETRY_BASE_MS * 2 ** (attempt - 1)))
      await refreshProjection(sessionId)
      continue
    }

    const payload: unknown = await response.json().catch(() => null)
    if (!response.ok) {
      const message =
        payload && typeof payload === 'object' && typeof (payload as { error?: unknown }).error === 'string'
          ? (payload as { error: string }).error
          : `Workshop intake update failed: ${response.status}`
      throw new Error(message)
    }
    if (!acceptSessionProjectionEnvelope(payload, '/api/session:workshop-intake')) {
      logger.warn('[workshop-intake] Session update returned no projection envelope')
    }
    return
  }
}
